import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  People as PeopleIcon,
  ArrowForward as ArrowForwardIcon,
  Replay as ReplayIcon,
  School as SchoolIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import useFeatureFlag from '../../hooks/useFeatureFlag';
import RightRail from '../layout/RightRail';
import RailPanel from '../layout/RailPanel';
import PracticeModesStack from './PracticeModesStack';

/**
 * Shared community right rail (dashboard + full Community Feed).
 */
export default function CommunityRightRail({
  onNavigateToDiscover,
  onNavigateToFeed,
  onNavigateToMarketplace,
  onStartInterview,
  onReviewLast,
  lastInterviewId = null,
  /** When true, hide "Open full feed" (already on /community/feed). */
  hideOpenFullFeed = false,
  /** Practice chip — default on; dashboard can omit until CTA is wired. */
  showPractice = true,
  /** Optional content after Placement prep (e.g. Answer Quality), before Suggested people. */
  afterPlacementSlot = null,
  testId = 'community-right-rail',
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enabled: campusPlacementEnabled } = useFeatureFlag('campus_placement');
  const { enabled: practiceMarketplaceEnabled } = useFeatureFlag('practice_interview_marketplace');
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        const res = await api.community.discover({ limit: 8 });
        if (cancelled) return;
        const pData = res?.data || res;
        const profiles = pData?.profiles || pData?.users || [];
        setPeople(Array.isArray(profiles) ? profiles.slice(0, 8) : []);
      } catch {
        if (!cancelled) setPeople([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, []);

  const displayName = (p) =>
    p.display_name || p.full_name ||
    [p.first_name, p.last_name].filter(Boolean).join(' ') ||
    'User';

  const personId = (p) => p.user_id || p.id;

  const openProfile = (p) => {
    const id = personId(p);
    if (id) navigate(`/community/profile/${id}`);
  };

  const handleStartPractice = () => {
    if (onStartInterview) onStartInterview();
    else navigate('/interview/start');
  };

  const handleDiscover = () => {
    if (onNavigateToDiscover) onNavigateToDiscover();
    else navigate('/community/discover');
  };

  const handleMarketplace = () => {
    if (onNavigateToMarketplace) onNavigateToMarketplace();
    else navigate('/marketplace');
  };

  const handlePracticeMode = (key) => {
    if (key === 'ai') handleStartPractice();
    else if (key === 'peers') handleDiscover();
    else if (key === 'experts') handleMarketplace();
  };

  return (
    <RightRail testId={testId}>
      <RailPanel testId="community-rail-ctas">
        {showPractice ? (
          <PracticeModesStack
            onSelect={handlePracticeMode}
            emphasized="ai"
            subtitle="AI, peers, or a booked interviewer — pick a lane."
          />
        ) : (
          <PracticeModesStack
            onSelect={handlePracticeMode}
            modes={['peers', 'experts']}
            emphasized={null}
            title="Practice with people"
            subtitle="Peers for free mocks, or book a verified interviewer."
          />
        )}

        {(lastInterviewId && onReviewLast) || onNavigateToFeed || hideOpenFullFeed ? (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {lastInterviewId && onReviewLast && (
              <Button
                size="small"
                variant="outlined"
                fullWidth
                startIcon={<ReplayIcon />}
                onClick={onReviewLast}
                data-testid="community-review-last"
                sx={{ textTransform: 'none', fontWeight: 600, justifyContent: 'flex-start' }}
              >
                Review last
              </Button>
            )}
            {!hideOpenFullFeed && onNavigateToFeed && (
              <Button
                size="small"
                variant="outlined"
                fullWidth
                endIcon={<ArrowForwardIcon />}
                onClick={onNavigateToFeed}
                sx={{ textTransform: 'none', fontWeight: 600, justifyContent: 'flex-start' }}
              >
                Open full feed
              </Button>
            )}
            {hideOpenFullFeed && (
              <Button
                size="small"
                variant="outlined"
                fullWidth
                startIcon={<PeopleIcon />}
                onClick={() => navigate('/community/connections')}
                sx={{ textTransform: 'none', fontWeight: 600, justifyContent: 'flex-start' }}
              >
                My connections
              </Button>
            )}
          </Stack>
        ) : null}
      </RailPanel>

      {campusPlacementEnabled && (
        <RailPanel testId="community-placement-promo" emphasized>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
            <SchoolIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={800} color="primary.main">
              Placement prep
            </Typography>
            <Chip label="Campus" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25, lineHeight: 1.45 }}>
            Drills, aptitude, and cohort tools for your college placement season.
          </Typography>
          <Stack spacing={1}>
            <Button
              fullWidth
              size="small"
              variant="contained"
              onClick={() => navigate('/campus/placement')}
              data-testid="community-placement-cta"
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Open Placement Hub
            </Button>
            {practiceMarketplaceEnabled && (
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={() => navigate('/practice/interviews/share-experience', {
                  state: { fromCampus: true },
                })}
                data-testid="community-share-interview-experience"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Share interview experience
              </Button>
            )}
          </Stack>
        </RailPanel>
      )}

      {!campusPlacementEnabled && practiceMarketplaceEnabled && (
        <RailPanel testId="community-share-experience-promo">
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Help peers practice
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25, lineHeight: 1.45 }}>
            Share questions from a recent interview so others can rehearse the real thing.
          </Typography>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={() => navigate('/practice/interviews/share-experience')}
            data-testid="community-share-interview-experience"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Share interview experience
          </Button>
        </RailPanel>
      )}

      {afterPlacementSlot && (
        <Box data-testid="community-rail-after-placement" sx={{ width: '100%', minWidth: 0 }}>
          {afterPlacementSlot}
        </Box>
      )}

      <RailPanel testId="community-suggested-people">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <PeopleIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={700}>
              Suggested people
            </Typography>
          </Stack>
          <Button size="small" onClick={handleDiscover} sx={{ textTransform: 'none', minWidth: 0 }}>
            View all
          </Button>
        </Stack>
        {loading ? (
          <Stack spacing={1}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: 1.5 }} />
            ))}
          </Stack>
        ) : people.length > 0 ? (
          <Stack spacing={1}>
            {people.map((p, idx) => {
              const id = personId(p);
              return (
                <Box
                  key={id || idx}
                  component="button"
                  type="button"
                  onClick={() => openProfile(p)}
                  disabled={!id}
                  data-testid="community-suggested-person"
                  aria-label={`View profile for ${displayName(p)}`}
                  sx={{
                    width: '100%',
                    minHeight: 44,
                    p: 1,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    textAlign: 'left',
                    cursor: id ? 'pointer' : 'default',
                    font: 'inherit',
                    color: 'inherit',
                    '&:hover': id ? { borderColor: 'primary.light', bgcolor: 'action.hover' } : undefined,
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      src={p.avatar_url}
                      sx={{ width: 36, height: 36, bgcolor: theme.palette.primary.main, flexShrink: 0 }}
                    >
                      {displayName(p)[0]?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {displayName(p)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {p.target_role || p.current_role || p.headline || 'Member'}
                      </Typography>
                      {p.experience_level && (
                        <Chip
                          label={p.experience_level}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5, height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                        />
                      )}
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No suggestions yet. Complete your profile to get matched.
          </Typography>
        )}
      </RailPanel>
    </RightRail>
  );
}

CommunityRightRail.propTypes = {
  onNavigateToDiscover: PropTypes.func,
  onNavigateToFeed: PropTypes.func,
  onNavigateToMarketplace: PropTypes.func,
  onStartInterview: PropTypes.func,
  onReviewLast: PropTypes.func,
  lastInterviewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideOpenFullFeed: PropTypes.bool,
  showPractice: PropTypes.bool,
  afterPlacementSlot: PropTypes.node,
  testId: PropTypes.string,
};
